import { useState } from 'react';
import LangEnum from '../../../../../lang/LangEnum';
import { useTranslation } from '../../../../../modules/translation/TranslationContext';
import { UseAsyncAutocompleteCreateOptionParams } from '../asyncAutocomplete.types';

const CREATE_OPTION_ID = '@@create-option';

export const useAsyncAutocompleteCreateOption = <T>({
  optionsMap,
  getOptionLabel,
  searchTerm,
  isSearching,
  onCreateOption,
}: UseAsyncAutocompleteCreateOptionParams<T>) => {
  const { translate } = useTranslation();
  const [inputText, setInputText] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const typedName = inputText.trim();
  const isSearchSettled = !isSearching && searchTerm.trim() === typedName;

  const hasOptionWithTypedName = [...optionsMap.values()].some(
    (option) => (getOptionLabel(option) || '').trim().toLowerCase() === typedName.toLowerCase(),
  );

  const canCreateOption =
    !!onCreateOption &&
    !!typedName &&
    isSearchSettled &&
    !isCreating &&
    !hasOptionWithTypedName &&
    !optionsMap.has(CREATE_OPTION_ID);

  const createOption = async () => {
    if (isCreating) {
      return;
    }
    setIsCreating(true);
    try {
      await onCreateOption?.(typedName);
    } finally {
      setIsCreating(false);
    }
  };

  return {
    createOption,
    trackInputText: setInputText,
    createOptionId: canCreateOption ? CREATE_OPTION_ID : undefined,
    createOptionLabel: translate(LangEnum.GLOBAL_ADD_OPTION, { value: typedName }),
  };
};
